import { TicksHistoryResponse, TicksStreamResponse } from '@deriv/api-types';
import EventEmitter from 'event-emitter-es6';
import { BinaryAPI } from 'src/binaryapi';
import { TCreateTickHistoryParams } from 'src/binaryapi/BinaryAPI';
import { Listener, OHLCStreamResponse, TMainStore, TQuote } from 'src/types';
import { TickHistoryFormatter } from '../TickHistoryFormatter';

export type TQuoteResponse = { quotes: TQuote[]; response: TicksHistoryResponse; error?: unknown };

class Subscription {
    _binaryApi: BinaryAPI;
    _emitter: EventEmitter;
    _request: TCreateTickHistoryParams;
    lastStreamEpoch?: number;
    _mainStore: TMainStore;
    static get EVENT_CHART_DATA() {
        return 'EVENT_CHART_DATA';
    }
    get contractInfo() {
        return this._mainStore.state.contractInfo;
    }
    get shouldFetchTickHistory() {
        return this._mainStore.state.shouldFetchTickHistory || false;
    }

    constructor(request: TCreateTickHistoryParams, api: BinaryAPI, mainStore: TMainStore) {
        this._binaryApi = api;
        this._request = request;
        this._emitter = new EventEmitter({ emitDelay: 0 });
        this._mainStore = mainStore;
    }

    async initialFetch() {
        const quotes_and_response = await this._startSubscribe(this._request);

        return quotes_and_response;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    pause() {}

    async resume() {
        if (this.lastStreamEpoch) {
            const tickHistoryRequest = {
                ...this._request,
                start: this.lastStreamEpoch,
            };

            const quotes_and_response = await this._startSubscribe(tickHistoryRequest);

            return quotes_and_response;
        }
    }

    forget() {
        this.lastStreamEpoch = undefined;
        this._emitter.off(Subscription.EVENT_CHART_DATA);
    }

    async _startSubscribe(_request: TCreateTickHistoryParams): Promise<TQuoteResponse> {
        throw new Error('Please override!');
    }

    _processHistoryResponse(response: TicksHistoryResponse) {
        console.log('[Subscription] ═══════════════════════════════════════════════════════════');
        console.log('[Subscription] 📨 PROCESSING API RESPONSE');
        
        if (response.error) {
            console.error('[Subscription] ❌ API Error:', response.error);
            throw response.error;
        }

        const quotes = TickHistoryFormatter.formatHistory(response);

        if (!quotes) {
            const message = `Unexpected response: ${response}`;
            console.error('[Subscription] ❌', message);
            throw new Error(message);
        }
        
        console.log('[Subscription] ✅ API Response Processed');
        console.log('[Subscription] Quotes received:', quotes.length);
        if (quotes.length > 0) {
            console.log('[Subscription] First quote time:', quotes[0]?.DT?.toLocaleString(), '| Epoch:', quotes[0]?.DT?.getTime());
            console.log('[Subscription] Last quote time:', quotes[quotes.length - 1]?.DT?.toLocaleString(), '| Epoch:', quotes[quotes.length - 1]?.DT?.getTime());
        }
        console.log('[Subscription] ═══════════════════════════════════════════════════════════');

        this.lastStreamEpoch = Subscription.getLatestEpoch(response);

        return quotes;
    }

    onChartData(callback: Listener) {
        this._emitter.on(Subscription.EVENT_CHART_DATA, callback);
    }

    static getLatestEpoch({ candles, history }: TicksHistoryResponse) {
        if (candles) {
            return candles[candles.length - 1].epoch;
        }

        if (history) {
            const { times = [] } = history;
            return times[times.length - 1];
        }
    }

    static getEpochFromTick(response: TicksStreamResponse | OHLCStreamResponse) {
        if ('tick' in response && response.tick) {
            return response.tick.epoch as number;
        }
        return (response as OHLCStreamResponse).ohlc.open_time;
    }
}

export default Subscription;
